import {
  DEFAULT_OG_IMAGE_PATH,
  SITE_NAME,
  getAbsoluteAssetUrl,
  getCanonicalUrl,
  getSiteOrigin,
} from "./pageSeo";

const trimText = (value, max = 5000) => {
  const text = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
};

export const buildOrganizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: getSiteOrigin(),
  logo: getAbsoluteAssetUrl("/web-app-manifest-512x512.png"),
  sameAs: [],
});

export const buildWebSiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: `${getSiteOrigin()}/`,
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${getSiteOrigin()}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
});

export const buildBreadcrumbJsonLd = (items = []) => {
  const itemListElement = items
    .filter((item) => item?.name && item?.pathname)
    .map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getCanonicalUrl(item.pathname),
    }));

  if (itemListElement.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
};

export const buildProductJsonLd = (product) => {
  if (!product) return null;

  const brandName =
    product.brand && typeof product.brand === "object"
      ? product.brand.name
      : product.brand;
  const categoryName =
    product.category && typeof product.category === "object"
      ? product.category.name
      : product.category;
  const image = Array.isArray(product.images)
    ? product.images
        .filter(Boolean)
        .map((src) => getAbsoluteAssetUrl(src))
    : [];
  const description =
    trimText(product.seoDescription || product.description, 5000) ||
    trimText(`Compare ${product.name} on ${SITE_NAME}.`, 5000);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description,
    url: getCanonicalUrl(`/product/${product._id || product.id}`),
    image: image.length > 0 ? image : [getAbsoluteAssetUrl(DEFAULT_OG_IMAGE_PATH)],
  };

  if (brandName) {
    jsonLd.brand = {
      "@type": "Brand",
      name: brandName,
    };
  }
  if (categoryName) {
    jsonLd.category = categoryName;
  }
  const model = String(product.model ?? "").trim();
  if (model) {
    jsonLd.model = model;
  }
  const liveAmount = Number(product.livePrice?.amount);
  const liveCurrency = product.livePrice?.currency;
  if (Number.isFinite(liveAmount) && liveCurrency) {
    jsonLd.offers = {
      "@type": "Offer",
      priceCurrency: liveCurrency,
      price: liveAmount,
      url: getCanonicalUrl(`/product/${product._id || product.id}`),
    };
  } else if (product.price != null && Number.isFinite(Number(product.price))) {
    jsonLd.offers = {
      "@type": "Offer",
      priceCurrency: "USD",
      price: Number(product.price),
      availability:
        product.active === false
          ? "https://schema.org/OutOfStock"
          : "https://schema.org/InStock",
      url: getCanonicalUrl(`/product/${product._id || product.id}`),
    };
  }

  return jsonLd;
};

export const buildListingOfferJsonLd = (listing) => {
  if (!listing) return null;

  const images = Array.isArray(listing.photos)
    ? listing.photos
        .filter(Boolean)
        .map((src) => getAbsoluteAssetUrl(src))
    : [];
  const description =
    trimText(listing.description, 5000) ||
    trimText(`${listing.title} on ${SITE_NAME} Marketplace.`, 5000);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: listing.title,
    description,
    url: getCanonicalUrl(`/marketplace/${listing._id || listing.id}`),
    image:
      images.length > 0
        ? images
        : [getAbsoluteAssetUrl(DEFAULT_OG_IMAGE_PATH)],
  };

  if (listing.price != null && Number.isFinite(Number(listing.price))) {
    jsonLd.offers = {
      "@type": "Offer",
      priceCurrency: "USD",
      price: Number(listing.price),
      availability:
        listing.status === "active"
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      url: getCanonicalUrl(`/marketplace/${listing._id || listing.id}`),
      itemCondition:
        listing.condition === "new"
          ? "https://schema.org/NewCondition"
          : "https://schema.org/UsedCondition",
    };
  }

  if (listing.seller?.username || listing.seller?.firstName) {
    jsonLd.seller = {
      "@type": "Person",
      name:
        listing.seller.username ||
        [listing.seller.firstName, listing.seller.lastName]
          .filter(Boolean)
          .join(" "),
    };
  }

  return jsonLd;
};
